import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Hero } from "@/components/Hero";
import { Services } from "@/components/Services";
import { Reviews } from "@/components/Reviews";
import { Gallery } from "@/components/Gallery";
import { FAQ } from "@/components/FAQ";
import { LeadForm } from "@/components/LeadForm";
import { ScrollProgress } from "@/components/ScrollProgress";
import { StickyCTA } from "@/components/StickyCTA";
import { InstagramFeed } from "@/components/InstagramFeed";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { SchemaMarkup } from "@/components/SchemaMarkup";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

const ANCHOR_MAP: Record<string, string> = {
  "contact-form": "contact",
  "services": "services",
  "about": "services",
};

const Index = () => {
  useSEO("home");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const anchor = searchParams.get("anchor");
    if (!anchor) return;

    const sectionId = ANCHOR_MAP[anchor];
    if (!sectionId) return;

    const timeout = setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("anchor");
        return next;
      }, { replace: true });
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchParams, setSearchParams]);

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SchemaMarkup type="LocalBusiness" />
      <ScrollProgress />
      <StickyCTA />
      <Header />
      <main id="main-content" className="flex-1">
        <Hero />
        <Services />
        <Gallery />
        <Reviews />
        <FAQ />
        
        {/* Contact Form Section */}
        <section id="contact" className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Request a Free Quote</h2>
              <p className="text-lg text-muted-foreground">
                Tell us about your vessel and project—we'll get right back to you.
              </p>
            </div>
            <LeadForm />
          </div>
        </section>

        <InstagramFeed />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-navy-900 to-ink-800 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready for a Clear View?</h2>
            <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
              Tell us about your vessel and project—we'll get right back to you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={scrollToContact}
                className="bg-white hover:bg-white/90 text-navy-900 hover:text-navy-800 shadow-xl hover:shadow-2xl font-bold px-10 py-4"
              >
                Request a Quote
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                asChild
                className="bg-transparent hover:bg-white/10 border-2 border-white/70 hover:border-white text-white font-semibold"
              >
                <a href="tel:6193774673">
                  <Phone className="mr-2 h-5 w-5" />
                  Call (619) 377-4673
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
